import type { AuthUser } from '@/features/auth/auth';
import { isAdministrative } from '@/lib/auth/admin-scopes';
import { hasPermission, type PermissionAction } from '@/lib/auth/permissions';
import {
  isProductWithheld,
  type TenantProducts,
} from '@/lib/auth/product-scopes';
import { canAccessSystemSection } from '@/lib/auth/roles';

interface RouteRequirement {
  scope: string;
  action: PermissionAction;
}

/**
 * Where the layout guard sends a refused navigation.
 *
 * Carries no entry in {@link ROUTE_REQUIREMENTS} on purpose: it has to remain
 * reachable by the very operators who are refused everywhere else, or the
 * guard would redirect to a page it then refuses, in a loop.
 */
export const FORBIDDEN_PATH = '/forbidden' as const;

// Routes that make up the admin "Tenant" section (plus the cross-tenant
// Platform admin under /settings/platform). Reachable only by a tenant admin
// and the global superuser — see canAccessSystemSection. Mirrors the sidebar
// gating in _layout.tsx so a non-admin cannot reach these screens by typing
// the URL directly.
//
// This list covers screens whose *section* is administrative regardless of the
// permission they happen to be keyed on. Routes whose required scope is itself
// administrative are caught structurally below, by isAdministrative, and do
// not need an entry here.
const SYSTEM_ADMIN_ROUTE_PREFIXES = [
  '/lock-engine',
  '/notifications',
  '/roles',
  '/settings', // Tenant Settings + /settings/branches + /settings/platform
  '/audit-logs',
] as const;

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

/**
 * The caller's own profile, reached from the avatar menu. Reading yourself is
 * not an act on the `users` scope — the page serves it from `/auth/me` — so
 * this path sits outside every administrative gate below. Without the
 * exemption, making `users` admin-only would take everyone else's profile page
 * away with it.
 */
function isOwnProfile(
  user: AuthUser | null | undefined,
  pathname: string
): boolean {
  return Boolean(user?.id) && pathname === `/users/${user?.id}`;
}

/**
 * Whether `pathname` belongs to the admin-only System section.
 *
 * Every `/users` path is administrative except {@link isOwnProfile}.
 */
function isSystemAdminRoute(
  user: AuthUser | null | undefined,
  pathname: string
): boolean {
  if (SYSTEM_ADMIN_ROUTE_PREFIXES.some((p) => matchesPrefix(pathname, p))) {
    return true;
  }
  return matchesPrefix(pathname, '/users') && !isOwnProfile(user, pathname);
}

const ROUTE_REQUIREMENTS: Array<[string, RouteRequirement]> = [
  ['/settings/platform/tenants', { scope: 'tenants', action: 'read' }],
  ['/users', { scope: 'users', action: 'read' }],
  ['/roles', { scope: 'roles', action: 'read' }],
  // Catalogue screens: everyone browses, only an admin adds. The create routes
  // need their own entry because the longest matching prefix wins, and the
  // list's `read` requirement would otherwise cover them.
  ['/financing-plans/new', { scope: 'financing_plans', action: 'create' }],
  ['/financing-plans', { scope: 'financing_plans', action: 'read' }],
  ['/device-models/new', { scope: 'device_models', action: 'create' }],
  ['/installment-settings', { scope: 'financing_plans', action: 'update' }],
  ['/lock-engine/policies', { scope: 'lock_engine', action: 'update' }],
  ['/lock-engine', { scope: 'lock_engine', action: 'read' }],
  ['/settings/branches', { scope: 'branches', action: 'read' }],
  ['/settings/policies', { scope: 'settings', action: 'read' }],
  ['/settings', { scope: 'tenant_settings', action: 'read' }],
  ['/value-lists', { scope: 'tenant_settings', action: 'update' }],
  ['/portfolio', { scope: 'contracts', action: 'read' }],
  ['/collections', { scope: 'payments', action: 'update' }],
  ['/audit-logs', { scope: 'audit_logs', action: 'read' }],
  ['/inventory/units/enroll', { scope: 'device_units', action: 'create' }],
  ['/inventory', { scope: 'device_units', action: 'read' }],
  ['/device-models', { scope: 'device_models', action: 'read' }],
  ['/applications/new', { scope: 'applications', action: 'create' }],
  ['/applications', { scope: 'applications', action: 'read' }],
  ['/customers/new', { scope: 'customers', action: 'create' }],
  ['/customers', { scope: 'customers', action: 'read' }],
  ['/merchants', { scope: 'merchants', action: 'read' }],
  ['/contracts', { scope: 'contracts', action: 'read' }],
  ['/payments/record', { scope: 'payments', action: 'create' }],
  ['/payments', { scope: 'payments', action: 'read' }],
  ['/notifications', { scope: 'notifications', action: 'read' }],
  // Deliberately NOT in SYSTEM_ADMIN_ROUTE_PREFIXES: backlog §2 and §6 put
  // Agents and Branch Managers on the support workflow, and the System section
  // is TenantAdmin-only. Its own `support` scope gates it instead.
  ['/support', { scope: 'support', action: 'read' }],
  // Same reasoning as /support: the operators who work complaints are Agents
  // and Branch Managers, not only Tenant Admins, so this stays out of the
  // System section and is gated on its own `feedback` scope.
  ['/feedback', { scope: 'feedback', action: 'read' }],
  ['/', { scope: 'dashboard', action: 'read' }],
];

const LANDING_PRIORITY: Array<{
  path: string;
  scope: string;
  action: PermissionAction;
}> = [
  { path: '/', scope: 'dashboard', action: 'read' },
  { path: '/portfolio', scope: 'contracts', action: 'read' },
  { path: '/applications', scope: 'applications', action: 'read' },
  { path: '/customers', scope: 'customers', action: 'read' },
  { path: '/contracts', scope: 'contracts', action: 'read' },
  { path: '/payments', scope: 'payments', action: 'read' },
];

function findRequirement(pathname: string): RouteRequirement | null {
  const sorted = [...ROUTE_REQUIREMENTS].sort(
    (a, b) => b[0].length - a[0].length
  );
  for (const [prefix, req] of sorted) {
    if (prefix === '/') {
      if (pathname === '/') return req;
      continue;
    }
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      // `/x/$id/edit` is a write on whatever `/x` reads. Derived rather than
      // listed because the id segment cannot be expressed as a prefix, and a
      // read requirement would otherwise let a non-admin open an edit form
      // the API will refuse to save.
      if (req.action === 'read' && pathname.endsWith('/edit')) {
        return { scope: req.scope, action: 'update' };
      }
      return req;
    }
  }
  return null;
}

/**
 * `enabledProducts` is the resolved tenant's product list, or `null` when it
 * hasn't resolved yet — the default, which leaves product-gated routes open.
 * Callers that can supply it (the layout guard, the sidebar, global search)
 * always should; see `./product-scopes`.
 */
export function canAccessRoute(
  user: AuthUser | null | undefined,
  pathname: string,
  enabledProducts: TenantProducts = null
): boolean {
  if (!user) return false;
  if (isOwnProfile(user, pathname)) return true;
  // The System section is gated on principal kind — tenant admin or superuser
  // — on top of any per-route permission requirement below.
  if (isSystemAdminRoute(user, pathname) && !canAccessSystemSection(user)) {
    return false;
  }
  const req = findRequirement(pathname);
  if (!req) return true;
  // A feature the tenant is not entitled to is refused for every principal,
  // superuser included — the grant says who may act, the entitlement says
  // whether the tenant has the feature at all.
  if (isProductWithheld(req.scope, enabledProducts)) return false;
  // A route asking for an administrative scope is admin-only by definition,
  // wherever it sits in the tree. The services refuse the call regardless;
  // this stops the portal from routing someone into a screen that will 403.
  if (isAdministrative(req.scope, req.action) && !canAccessSystemSection(user)) {
    return false;
  }
  return hasPermission(user, req.scope, req.action);
}

export function getLandingPath(user: AuthUser | null | undefined): string {
  if (!user) return '/login';
  for (const candidate of LANDING_PRIORITY) {
    if (hasPermission(user, candidate.scope, candidate.action)) {
      return candidate.path;
    }
  }
  return '/notifications';
}
